import React from "react";
import { GraphProps } from "./graphUtils";
import SimpleGraph from "./SimpleGraph";
import ComplexGraph, { ComplexGraphData } from "./ComplexGraph";
import MinGraph, { MinGraphData } from "./MinGraph";
import MaxGraph, { MaxGraphData } from "./MaxGraph";
import StreakGraph, { StreakGraphData } from "./StreakGraph";
import LtwGraph, { LtwGraphData } from "./LtwGraph"; // ← ADD THIS
import TrendGraph, { TrendGraphData } from "./TrendGraph";
import AvgGraph, { AvgGraphData } from "./AvgGraph";
import SumGraph, { SumGraphData } from "./SumGraph";
import RankGraph, { RankGraphData } from "./RankGraph";
import CountGraph, { CountGraphData } from "./CountGraph";

// ── Extended props ────────────────────────────────────────────────────────────
interface GraphRendererProps extends GraphProps {
  queryType:    string | undefined;
  complexData?: ComplexGraphData;
  minData?:     MinGraphData;
  maxData?:     MaxGraphData;
  streakData?:  StreakGraphData;
  ltwData?:     LtwGraphData;   // ← ADD THIS
  trendData?:   TrendGraphData;
  avgData?:     AvgGraphData;
  sumData?:     SumGraphData;
  rankData?:    RankGraphData;
  countData?:   CountGraphData;
  statLabel?:   string;
}

const GraphRenderer: React.FC<GraphRendererProps> = ({
  queryType,
  complexData,
  minData,
  maxData,
  streakData,
  ltwData,      // ← ADD THIS
  trendData,    // ← ADD THIS
  avgData,      // ← ADD THIS
  sumData,      // ← ADD THIS
  rankData,     // ← ADD THIS
  countData,    // ← ADD THIS
  statLabel,
  ...graphProps
}) => {
  const qt = queryType?.toLowerCase();

  switch (qt) {
    case "simple":
    case "basic":
      return <SimpleGraph {...graphProps} />;

    case "complex":
      if (!complexData) {
        console.warn("GraphRenderer: query_type=complex but no complexData provided");
        return null;
      }
      return <ComplexGraph {...complexData} chartReady={graphProps.chartReady} />;

    case "min":
      if (!minData) {
        console.warn("GraphRenderer: query_type=min but no minData provided");
        return null;
      }
      return (
        <MinGraph
          data={minData}
          chartReady={graphProps.chartReady}
          statLabel={statLabel}
        />
      );

    case "max":
      if (!maxData) {
        console.warn("GraphRenderer: query_type=max but no maxData provided");
        return null;
      }
      return (
        <MaxGraph
          data={maxData}
          chartReady={graphProps.chartReady}
          statLabel={statLabel}
        />
      );

    case "streak":
      if (!streakData) {
        console.warn("GraphRenderer: query_type=streak but no streakData provided");
        return null;
      }
      return (
        <StreakGraph
          data={streakData}
          chartReady={graphProps.chartReady}
          statLabel={statLabel}
        />
      );

    // ── LTW ─────────────────────────────────────────────────────────────────
    case "ltw":
      if (!ltwData) {
        console.warn("GraphRenderer: query_type=ltw but no ltwData provided");
        return null;
      }
      return (
        <LtwGraph
          data={ltwData}
          chartReady={graphProps.chartReady}
          statLabel={statLabel}
          teamColor={graphProps.teamColor}
        />
      );

    case "trend":
      if (!trendData) {
        console.warn("GraphRenderer: query_type=trend but no trendData provided");
        return null;
      }
      return (
        <TrendGraph
          data={trendData}
          chartReady={graphProps.chartReady}
          statLabel={statLabel}
        />
      );

    case "avg":
      if (!avgData) {
        console.warn("GraphRenderer: query_type=avg but no avgData provided");
        return null;
      }
      return (
        <AvgGraph
          data={avgData}
          chartReady={graphProps.chartReady}
          statLabel={statLabel}
        />
      );

    case "sum":
      if (!sumData) {
        console.warn("GraphRenderer: query_type=sum but no sumData provided");
        return null;
      }
      return (
        <SumGraph
          data={sumData}
          chartReady={graphProps.chartReady}
          statLabel={statLabel}
        />
      );

    case "rank":
      if (!rankData) {
        console.warn("GraphRenderer: query_type=rank but no rankData provided");
        return null;
      }
      return (
        <RankGraph
          data={rankData}
          chartReady={graphProps.chartReady}
          statLabel={statLabel}
        />
      );

    case "count":
      if (!countData) {
        console.warn("GraphRenderer: query_type=count but no countData provided");
        return null;
      }
      return (
        <CountGraph
          data={countData}
          chartReady={graphProps.chartReady}
          statLabel={statLabel}
        />
      );

    // default:
    //   return <SimpleGraph {...graphProps} />;
  }
};

export default GraphRenderer;